/**
 * Phase 9A — Brief Viewer.
 *
 * Authenticated, in-app reader for the customer's sealed Concierge
 * Brief. Fetches `GET /api/events/:eventId/brief/html` (a new
 * customer-authenticated route added in Phase 9A) and renders the
 * returned HTML inside a sandboxed iframe so the brief markup cannot
 * touch the host app's DOM or stylesheet.
 *
 * Strict scope:
 *   * No PDF download.
 *   * No share button (the brief is the customer's; sharing is not the
 *     customer's job).
 *   * No "contact this vendor" or "choose" actions — bridging belongs
 *     to a later phase.
 *   * No share_token surfacing. The backend resolves the latest sealed
 *     brief by event ownership; the token never reaches the browser.
 *
 * Failure modes:
 *   * 401 / 403 → "You don't have access to this brief."
 *   * 404       → "Your Brief isn't ready yet" (still in draft, or no
 *                  sealed revision exists).
 *   * other     → "We couldn't load the brief right now."
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BriefViewerPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [html, setHtml] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | not_ready | forbidden | error

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!eventId) return;
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/events/${eventId}/brief/html`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          responseType: 'text',
          transformResponse: [(d) => d],
        });
        if (cancelled) return;
        setHtml(res.data || '');
        setStatus('ready');
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status;
        if (code === 401 || code === 403) setStatus('forbidden');
        else if (code === 404) setStatus('not_ready');
        else setStatus('error');
      }
    }
    load();
    return () => { cancelled = true; };
  }, [eventId]);

  const goBack = () => {
    if (eventId) navigate(`/events/${eventId}`);
    else navigate('/customer-dashboard');
  };

  const headerBar = (
    <div className="max-w-3xl mx-auto px-5 pt-6 pb-3 flex items-center">
      <button
        type="button"
        onClick={goBack}
        className="inline-flex items-center gap-1 text-sm font-medium text-violet-600 hover:text-violet-700"
        data-testid="brief-viewer-back"
      >
        <ChevronLeft className="w-4 h-4" aria-hidden />
        Back to event
      </button>
    </div>
  );

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-white" data-testid="brief-viewer-loading">
        {headerBar}
        <div className="max-w-3xl mx-auto px-5 pt-4">
          <div className="h-4 w-32 bg-gray-100 rounded animate-pulse mb-3" />
          <div className="h-3 w-full bg-gray-50 rounded animate-pulse mb-2" />
          <div className="h-3 w-5/6 bg-gray-50 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (status === 'forbidden') {
    return (
      <div className="min-h-screen bg-white" data-testid="brief-viewer-forbidden">
        {headerBar}
        <div className="max-w-3xl mx-auto px-5 pt-6">
          <p className="text-sm text-gray-700">
            You don&apos;t have access to this brief.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'not_ready') {
    return (
      <div className="min-h-screen bg-white" data-testid="brief-viewer-not-ready">
        {headerBar}
        <div className="max-w-3xl mx-auto px-5 pt-6">
          <p className="text-sm text-gray-700">
            Your Brief isn&apos;t ready yet. Your Concierge will let you know when it is.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-white" data-testid="brief-viewer-error">
        {headerBar}
        <div className="max-w-3xl mx-auto px-5 pt-6">
          <p className="text-sm text-gray-700">
            We couldn&apos;t load the brief right now. Please try again in a moment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="brief-viewer">
      {headerBar}
      <div className="max-w-3xl mx-auto px-5 pb-12">
        {/* Sandboxed iframe — the rendered brief HTML is a full document
            shipped by the backend renderer. Using srcDoc keeps it
            isolated from the host CSS and prevents any script in the
            rendered output from touching the host window. */}
        <iframe
          title="Your Brief"
          srcDoc={html || ''}
          sandbox="allow-same-origin allow-scripts"
          className="w-full min-h-[80vh] border border-gray-100 rounded-lg"
          data-testid="brief-viewer-frame"
        />
      </div>
    </div>
  );
}
